import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { ModulesWithAvailableActivityList } from '../../business/available-activities/available-activities.component';
import { logger } from 'src/app/common/services/logging/logging.service';
import { Platform } from '@ionic/angular';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  catchError,
  concatMap,
  defaultIfEmpty,
  defer,
  filter,
  finalize,
  first,
  from,
  map,
  of,
  take,
  tap,
  timeout
} from 'rxjs';
import { App } from '@capacitor/app';
import { ResponsiveDesignService } from 'src/app/common/services/responsive-design/responsive-design.service';

export enum BackPriority {
  DIALOG = 50,
  PAGE_UI = 40,
  OVERLAY = 30,
  MENU = 20,
  NAVIGATION = 10,
  APP_EXIT = 0
}

export interface RouteChangeEvent {
  from?: string;
  to: string;
  trigger: 'imperative' | 'popstate' | 'hashchange';
}

interface BackHandlerEntry {
  id: string;
  priority: number;
  handler: () => Observable<boolean>;
  clearOnRouteChange: boolean;
  token: number;
}

/**
 * Higher than Ionic's overlay (100) and menu (99) handlers so this service
 * fully owns Android Back. Not calling processNextHandler prevents
 * ion-router-outlet from also popping the route.
 */
const ANDROID_BACK_BUTTON_PRIORITY = 999;
const HANDLER_TIMEOUT_MS = 2000;
const EXIT_INTERVAL_MS = 1500;

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  private static router: Router;
  private static ngZone: NgZone;

  private readonly handlers = new Map<string, BackHandlerEntry>();
  /** Handler ids per priority, index 0 = most recently registered (LIFO). */
  private readonly handlersByPriority = new Map<number, string[]>();
  private handlerSeq = 0;

  readonly routeChange$ = new Subject<RouteChangeEvent>();
  readonly currentUrl$ = new BehaviorSubject<string>('');

  private lastUrl?: string;
  private routeStack: string[] = [];
  private poppingRoute = false;
  private resolvingBack = false;
  private lastBackTime = 0;
  private backButtonSub?: Subscription;

  constructor(
    ngZone: NgZone,
    private platform: Platform,
    private router: Router,
    private srvRpDesign: ResponsiveDesignService
  ) {
    NavigationService.ngZone = ngZone;
    NavigationService.router = router;
    this.currentUrl$.next(this.router.url);
    this.platform.ready().then(() => this.initBackHandler());
    this.initRouteListener();
  }

  public init() {
    // Nothing to do, assume the ngZone is preload
  }

  /**
   * Core navigation method with proper error handling for banking flows
   */
  static goto(router: Router, url: string, extras?: NavigationExtras, delayMs: number = 100): Promise<boolean> {
    logger.info(`Navigating to ${url}`);

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        this.ngZone.run(() => {
          router.navigate([url], extras)
            .then(success => {
              if (success) {
                logger.info(`Navigation to ${url} successful`);
                resolve(true);
              } else {
                logger.warn(`Navigation to ${url} blocked or failed`);
                resolve(false);
              }
            })
            .catch(error => {
              logger.error(`Navigation to ${url} failed:`, error);
              reject(error);
            });
        });
      }, delayMs);
    });
  }

  static gotoActivityPage(router: Router, module: ModulesWithAvailableActivityList): Promise<boolean> {
    return this.goto(router, "/available-activities", { queryParams: { module } });
  }

  static gotoDashboard(router: Router): Promise<boolean> {
    return this.goto(router, "/dashboard");
  }

  /**
   * This is a special navigation method that is used to force destroy/recreate of the target route.
   * Bounce through activity-reload to force destroy/recreate of the target route.
   */
  static gotoViaActivityReload(returnTo: string): Promise<boolean> {
    const cleaned = returnTo.replace(/^\/+/, '');
    return this.goto(NavigationService.router, '/activity-reload', {
      queryParams: { returnTo: cleaned },
    }, 0);
  }

  static gotoLogin(router: Router): Promise<boolean> {
    return this.goto(router, "/login");
  }

  static gotoToCorporateLanding(router: Router): Promise<boolean> {
    return this.goto(router, "/corporate/make-payment");
  }

  static gotoPayrollLanding(router: Router): Promise<boolean> {
    return this.goto(router, "/payroll-department");
  }

  static isOnDashboardPage(router: Router): boolean {
    return router.url.includes("/dashboard");
  }


  /**
   * Navigate after dialog dismissal with proper completion handling
   */
  static gotoAfterDialog(router: Router, url: string, extras?: NavigationExtras): Promise<boolean> {
    return this.goto(router, url, extras, 150); // Longer delay for dialogs
  }

  /**
   * Register an Android Back handler.
   *
   * @param id Stable identity. Re-registering the same id replaces the previous
   *           handler and moves it to the top of that priority (LIFO).
   *           Omit only for true one-shot handlers; prefer an explicit id.
   * @returns Unregister callback. Also available via unregisterBackHandler(id).
   */
  registerBackHandler(
    priority: number,
    handler: () => Observable<boolean>,
    id?: string
  ): () => void {
    const handlerId = id ?? `back-${++this.handlerSeq}`;
    const token = ++this.handlerSeq;

    if (this.handlers.has(handlerId)) {
      this.removeFromPriorityIndex(handlerId);
    }

    const entry: BackHandlerEntry = {
      id: handlerId,
      priority,
      handler,
      clearOnRouteChange: priority >= BackPriority.OVERLAY,
      token
    };

    this.handlers.set(handlerId, entry);
    const list = this.handlersByPriority.get(priority) ?? [];
    list.unshift(handlerId);
    this.handlersByPriority.set(priority, list);

    return () => {
      const current = this.handlers.get(handlerId);
      if (current?.token === token) {
        this.unregisterBackHandler(handlerId);
      }
    };
  }

  unregisterBackHandler(id: string): void {
    if (!this.handlers.has(id)) return;
    this.removeFromPriorityIndex(id);
    this.handlers.delete(id);
  }

  private initBackHandler() {
    if (!this.srvRpDesign.isMobileAndroid()) return;

    this.backButtonSub?.unsubscribe();
    this.backButtonSub = this.platform.backButton.subscribeWithPriority(
      ANDROID_BACK_BUTTON_PRIORITY,
      () => this.resolveBack()
    );

    this.registerMenuHandler();
    this.registerNavigationHandler();
    this.registerAppExitHandler();
  }

  private resolveBack() {
    if (this.resolvingBack) return;

    this.resolvingBack = true;
    NavigationService.ngZone.run(() => {
      this.resolveBack$()
        .pipe(finalize(() => { this.resolvingBack = false; }))
        .subscribe({
          error: err => logger?.error('Android back resolver failed', err)
        });
    });
  }

  private resolveBack$(): Observable<boolean> {
    const entries = this.getHandlersInResolveOrder();

    return from(entries).pipe(
      concatMap(entry =>
        defer(() => {
          try {
            return entry.handler() ?? of(false);
          } catch (err) {
            logger?.error(`Android back handler '${entry.id}' threw`, err);
            return of(false);
          }
        }).pipe(
          take(1),
          timeout({ first: HANDLER_TIMEOUT_MS }),
          catchError(err => {
            logger?.error(`Android back handler '${entry.id}' failed`, err);
            return of(false);
          }),
          defaultIfEmpty(false),
          tap(handled => {
            if (handled && entry.priority > BackPriority.APP_EXIT) {
              this.lastBackTime = 0;
            }
          })
        )
      ),
      first(handled => handled === true, false)
    );
  }

  private getHandlersInResolveOrder(): BackHandlerEntry[] {
    const priorities = Array.from(this.handlersByPriority.keys()).sort((a, b) => b - a);
    const ordered: BackHandlerEntry[] = [];

    for (const priority of priorities) {
      for (const id of this.handlersByPriority.get(priority) ?? []) {
        const entry = this.handlers.get(id);
        if (entry) ordered.push(entry);
      }
    }

    return ordered;
  }

  private removeFromPriorityIndex(id: string) {
    const entry = this.handlers.get(id);
    if (!entry) return;

    const list = this.handlersByPriority.get(entry.priority);
    if (!list) return;

    const next = list.filter(existingId => existingId !== id);
    if (next.length) {
      this.handlersByPriority.set(entry.priority, next);
    } else {
      this.handlersByPriority.delete(entry.priority);
    }
  }

  private clearRouteScopedHandlers() {
    const toRemove = Array.from(this.handlers.values())
      .filter(entry => entry.clearOnRouteChange)
      .map(entry => entry.id);

    for (const id of toRemove) {
      this.unregisterBackHandler(id);
    }
  }

  private initRouteListener() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const to = event.urlAfterRedirects;
        const from = this.lastUrl;
        const trigger = this.router.getCurrentNavigation()?.trigger ?? 'imperative';

        this.routeChange$.next({ from, to, trigger });
        this.currentUrl$.next(to);
        this.syncRouteStack(from, to, trigger);
        this.lastUrl = to;
      });
  }

  private syncRouteStack(
    from: string | undefined,
    to: string,
    trigger: RouteChangeEvent['trigger']
  ) {
    const urlChanged = from !== to;

    if (this.poppingRoute) {
      this.poppingRoute = false;
      if (this.routeStack.length === 0) {
        this.routeStack = [to];
      }
      if (urlChanged) this.clearRouteScopedHandlers();
      return;
    }

    if (trigger === 'popstate') {
      this.syncStackFromPopstate(to);
      if (urlChanged) this.clearRouteScopedHandlers();
      return;
    }

    if (this.isLoginUrl(to) || this.isDashboardUrl(to)) {
      this.routeStack = [to];
      if (urlChanged) this.clearRouteScopedHandlers();
      return;
    }

    if (this.routeStack[this.routeStack.length - 1] === to) {
      return;
    }

    this.routeStack.push(to);
    if (urlChanged) this.clearRouteScopedHandlers();
  }

  private syncStackFromPopstate(to: string) {
    const match = this.routeStack.lastIndexOf(to);
    if (match >= 0) {
      this.routeStack = this.routeStack.slice(0, match + 1);
      return;
    }
    this.routeStack = [to];
  }

  private registerMenuHandler() {
    this.registerBackHandler(
      BackPriority.MENU,
      () => this.srvRpDesign.getSidebarNav().pipe(
        take(1),
        tap(show => { if (show) this.srvRpDesign.setSidebarNav(false); }),
        map(show => show)
      ),
      'menu:sidebar'
    );
  }

  private registerNavigationHandler() {
    this.registerBackHandler(
      BackPriority.NAVIGATION,
      () => this.handleRouteBack(),
      'navigation:route'
    );
  }

  private handleRouteBack(): Observable<boolean> {
    const current = this.currentUrl$.value || this.router.url;

    if (this.isAppRoot(current)) {
      return of(false);
    }

    if (this.routeStack.length >= 2) {
      const previous = this.routeStack[this.routeStack.length - 2];
      return this.navigateBackTo(previous);
    }

    if (!this.isDashboardUrl(current)) {
      this.poppingRoute = true;
      this.routeStack = [];
      return from(this.router.navigateByUrl('/dashboard')).pipe(
        map(success => !!success),
        catchError(err => {
          this.poppingRoute = false;
          logger?.error('Android back navigation to dashboard failed', err);
          return of(false);
        })
      );
    }

    return of(false);
  }

  private navigateBackTo(previous: string): Observable<boolean> {
    const removed = this.routeStack.pop();
    this.poppingRoute = true;

    return from(this.router.navigateByUrl(previous, { replaceUrl: true })).pipe(
      map(success => {
        if (!success) {
          this.poppingRoute = false;
          if (removed) this.routeStack.push(removed);
          return false;
        }
        return true;
      }),
      catchError(err => {
        this.poppingRoute = false;
        if (removed) this.routeStack.push(removed);
        logger?.error('Android back navigation failed', err);
        return of(false);
      })
    );
  }

  private registerAppExitHandler() {
    this.registerBackHandler(
      BackPriority.APP_EXIT,
      () => {
        const now = Date.now();

        if (this.lastBackTime > 0 && now - this.lastBackTime < EXIT_INTERVAL_MS) {
          App.exitApp();
          return of(true);
        }

        this.lastBackTime = now;
        return of(false);
      },
      'app:exit'
    );
  }

  private isAppRoot(url: string): boolean {
    return this.isDashboardUrl(url) || this.isLoginUrl(url) || this.pathOnly(url) === '/' || this.pathOnly(url) === '/secured';
  }

  private isDashboardUrl(url: string): boolean {
    const path = this.pathOnly(url);
    return path === '/dashboard' || path.endsWith('/dashboard');
  }

  private isLoginUrl(url: string): boolean {
    return this.pathOnly(url).includes('/login');
  }

  private pathOnly(url: string): string {
    return url.split('?')[0].split('(')[0];
  }

}